import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import KinAvatar from "@/components/KinAvatar";

const REVIEW_URL = "itms-apps://itunes.apple.com/app/id6763621685?action=write-review";

interface ReviewPromptProps {
  onDismiss: () => void;
}

const markPrompted = async () => {
  try {
    await supabase.auth.updateUser({ data: { review_prompted: true } });
  } catch (err) {
    console.error("review_prompted update failed (non-fatal):", err);
  }
};

const trackFeedback = async (userId: string, feedbackType: string) => {
  try {
    await supabase
      .from("response_feedback")
      .insert({ user_id: userId, feedback_type: feedbackType });
  } catch {
    // swallow
  }
};

const ReviewPrompt = ({ onDismiss }: ReviewPromptProps) => {
  const { user } = useAuth();

  const handleDismiss = () => {
    if (user?.id) trackFeedback(user.id, "app_store_rate_dismissed");
    void markPrompted();
    onDismiss();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (user?.id) trackFeedback(user.id, "app_store_rate_dismissed");
        void markPrompted();
        onDismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss, user?.id]);

  const handleRate = () => {
    if (user?.id) trackFeedback(user.id, "app_store_rate_yes");
    void markPrompted();
    window.location.href = REVIEW_URL;
    onDismiss();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Rate Kin on the App Store"
      onClick={handleDismiss}
      className="fixed inset-0 flex items-center justify-center animate-fade-in"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        zIndex: 60,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-scale-in"
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: 20,
          padding: "28px 24px",
          boxShadow: "0 20px 60px rgba(28, 26, 62, 0.25)",
          maxWidth: 300,
          width: "calc(100% - 48px)",
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <KinAvatar size="md" variant="dark" />
        </div>

        <h2
          className="font-display"
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: "#1C1A3E",
            margin: 0,
            marginBottom: 6,
            lineHeight: 1.25,
          }}
        >
          Loving Kin so far?
        </h2>
        <p
          className="font-body"
          style={{
            fontSize: 14,
            color: "#54525C",
            lineHeight: 1.4,
            margin: 0,
            marginBottom: 24,
          }}
        >
          A quick rating helps other moms find us 💜
        </p>

        <button
          type="button"
          onClick={handleRate}
          className="font-body"
          style={{
            width: "100%",
            backgroundColor: "#5050C8",
            color: "#FFFFFF",
            fontWeight: 600,
            fontSize: 15,
            padding: "14px 24px",
            border: "none",
            borderRadius: 12,
            cursor: "pointer",
          }}
        >
          Rate Kin on the App Store
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="font-body"
          style={{
            width: "100%",
            backgroundColor: "#F0F0FF",
            color: "#54525C",
            fontWeight: 600,
            fontSize: 14,
            padding: "12px 24px",
            border: "none",
            borderRadius: 12,
            cursor: "pointer",
            marginTop: 10,
          }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default ReviewPrompt;
